import { useEffect, useRef, useState } from "react";

import { Box, Grid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { InputLabel, TextInput } from "components";
import { SwissCanton } from "types";
import { AddressUtils, AppUtils } from "utils";

interface PostalAndCityProps<T> {
  form: UseFormReturnType<T>;
  postalFieldName: keyof T | string;
  cityFieldName: keyof T | string;
  cantonFieldName?: keyof T | string;
  cantonOptions?: SwissCanton[];
  disabled?: boolean;
  label?: string;
}

export const PostalAndCity = <T,>({
  form,
  postalFieldName,
  cityFieldName,
  cantonFieldName,
  cantonOptions,
  disabled = false,
  label,
}: PostalAndCityProps<T>) => {
  const { t } = useTranslation();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [cantonLookupInput, setCantonLookupInput] = useState<{
    postal: string;
    city: string;
  } | null>(null);
  const [debouncedCantonLookupInput] = useDebouncedValue(cantonLookupInput, 300);
  const cantonLookupAbortControllerRef = useRef<AbortController | null>(null);

  const getFieldKey = (fieldName: string) => {
    return editingField === fieldName ? fieldName : form.key(fieldName);
  };

  const getTextFieldValue = (fieldName: keyof T | string): string => {
    const value = form.getValues()?.[fieldName as keyof T];
    return typeof value === "string" ? value : "";
  };

  const queueCantonLookup = (postalValue: string, cityValue: string) => {
    if (!cantonFieldName || !cantonOptions) {
      return;
    }

    const normalizedPostalValue = postalValue.trim();
    const normalizedCityValue = AddressUtils.getCanonicalCityName(cityValue)?.trim();
    if (normalizedPostalValue.length < 4 || !normalizedCityValue) {
      return;
    }

    setCantonLookupInput((currentValue) => {
      if (
        currentValue?.postal === normalizedPostalValue &&
        currentValue.city === normalizedCityValue
      ) {
        return currentValue;
      }

      return {
        postal: normalizedPostalValue,
        city: normalizedCityValue,
      };
    });
  };

  const handlePostalChange = (value: string) => {
    form.setFieldValue(postalFieldName, value as any);

    const city = AddressUtils.getCityFromPostal(value);
    if (city) {
      form.setFieldValue(cityFieldName, city as any, { forceUpdate: true });
    }

    queueCantonLookup(value, city ?? getTextFieldValue(cityFieldName));
  };

  const handleCityChange = (value: string) => {
    form.setFieldValue(cityFieldName, value as any);

    const postal = AddressUtils.getPostalFromCity(value);
    if (postal) {
      form.setFieldValue(postalFieldName, postal as any, { forceUpdate: true });
    }

    queueCantonLookup(postal ?? getTextFieldValue(postalFieldName), value);
  };

  const postalValue = getTextFieldValue(postalFieldName);
  const cityValue = getTextFieldValue(cityFieldName);
  const cantonCode = cantonFieldName
    ? AppUtils.getSwissCantonCode(
        form.getValues()?.[cantonFieldName as keyof T] as SwissCanton | string | null | undefined,
      )
    : undefined;

  useEffect(() => {
    return () => cantonLookupAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!cantonFieldName || !cantonOptions) {
      return;
    }

    const normalizedPostalValue = postalValue.trim();
    const normalizedCityValue = AddressUtils.getCanonicalCityName(cityValue)?.trim();
    if (normalizedPostalValue.length < 4 || !normalizedCityValue) {
      return;
    }

    setCantonLookupInput((currentValue) => {
      if (
        currentValue?.postal === normalizedPostalValue &&
        currentValue.city === normalizedCityValue
      ) {
        return currentValue;
      }

      return {
        postal: normalizedPostalValue,
        city: normalizedCityValue,
      };
    });
  }, [cantonCode, cantonFieldName, cantonOptions, cityValue, postalValue]);

  useEffect(() => {
    if (!debouncedCantonLookupInput || !cantonFieldName || !cantonOptions) {
      return;
    }

    cantonLookupAbortControllerRef.current?.abort();
    const controller = new AbortController();
    cantonLookupAbortControllerRef.current = controller;

    void AddressUtils.getSwissCantonFromPostalAndCity(
      debouncedCantonLookupInput.postal,
      debouncedCantonLookupInput.city,
      controller.signal,
    )
      .then((nextCanton) => {
        if (!nextCanton) {
          return;
        }

        const currentPostalFieldValue = form.getValues()?.[postalFieldName as keyof T];
        const currentCityFieldValue = form.getValues()?.[cityFieldName as keyof T];
        const currentPostalValue =
          typeof currentPostalFieldValue === "string" ? currentPostalFieldValue.trim() : "";
        const currentCityValue =
          typeof currentCityFieldValue === "string"
            ? AddressUtils.getCanonicalCityName(currentCityFieldValue)?.trim() ?? ""
            : "";

        if (
          currentPostalValue !== debouncedCantonLookupInput.postal ||
          currentCityValue.toLowerCase() !== debouncedCantonLookupInput.city.toLowerCase()
        ) {
          return;
        }

        const cantonOption =
          AppUtils.getSwissCantonOption(nextCanton.code, cantonOptions) ??
          cantonOptions.find(
            (option) =>
              option.label === nextCanton.name ||
              option.value === nextCanton.name,
          );
        if (!cantonOption) {
          return;
        }

        const currentCantonCode = AppUtils.getSwissCantonCode(
          form.getValues()?.[cantonFieldName as keyof T] as
            | SwissCanton
            | string
            | null
            | undefined,
        );
        if (currentCantonCode === cantonOption.value) {
          return;
        }

        form.setFieldValue(cantonFieldName, cantonOption as any, { forceUpdate: true });
      })
      .catch((error) => {
        // Best-effort autofill should stay silent on network failures.
        if ((error as DOMException)?.name === "AbortError") {
          return;
        }
      });

    return () => controller.abort();
  }, [cantonFieldName, cantonOptions, cityFieldName, debouncedCantonLookupInput, form, postalFieldName]);

  return (
    <Box>
      <InputLabel label={label ?? t("user.address.postalAndCity.label")} required={true} />
      <Grid>
        <Grid.Col span={4}>
          <TextInput
            key={getFieldKey(postalFieldName as string)}
            {...form.getInputProps(postalFieldName)}
            maxLength={4}
            required={true}
            onFocus={() => setEditingField(postalFieldName as string)}
            onChange={(event) => handlePostalChange(event.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
        <Grid.Col span={8}>
          <TextInput
            key={getFieldKey(cityFieldName as string)}
            {...form.getInputProps(cityFieldName)}
            required={true}
            onFocus={() => setEditingField(cityFieldName as string)}
            onChange={(event) => handleCityChange(event.currentTarget.value)}
            disabled={disabled}
          />
        </Grid.Col>
      </Grid>
    </Box>
  );
};
